import { useEffect, useState, type ReactNode } from "react";

import { ROTULO_FUSO, doCampo, paraCampo } from "../lib/tempo";

export interface Intervalo {
  rotulo: string;
  de: Date;
  ate: Date;
}

const PRESETS: Array<{ rotulo: string; horas: number }> = [
  { rotulo: "1 h", horas: 1 },
  { rotulo: "6 h", horas: 6 },
  { rotulo: "24 h", horas: 24 },
  { rotulo: "7 d", horas: 168 },
];

export const PERSONALIZADO = "personalizado";

/**
 * Espelha o `MAX_SPAN` de `services/read.py`.
 *
 * Validado aqui só para o erro aparecer junto ao campo em vez de voltar do
 * servidor como `422`. Quem manda continua a ser o servidor — isto é conforto,
 * não é a regra.
 */
const MAX_DIAS = 31;

export function intervaloDe(rotulo: string, horas: number, agora = new Date()): Intervalo {
  return { rotulo, de: new Date(agora.getTime() - horas * 3600_000), ate: agora };
}

export const INTERVALO_INICIAL = (): Intervalo => intervaloDe("6 h", 6);

type Leitura = { ok: true; data: Date } | { ok: false; motivo: "vazio" | "invalida" };

/** Distingue "ainda não escreveste" de "isso não é uma data" — as mensagens
 *  de erro não são a mesma coisa e o campo cheio com um ano de 6 dígitos não
 *  pode dizer que está vazio. */
function ler(valor: string): Leitura {
  if (valor === "") return { ok: false, motivo: "vazio" };
  const d = doCampo(valor);
  return d === null ? { ok: false, motivo: "invalida" } : { ok: true, data: d };
}

/**
 * Intervalo do histórico: quatro atalhos, ou datas à escolha.
 *
 * Os atalhos são relativos a agora e recalculam-se ao clicar; o intervalo livre
 * é absoluto e fica onde está — é o que serve para olhar para uma janela
 * concreta da campanha, ou para o momento em que uma boia se calou.
 *
 * Sem balde à escolha: a API escolhe-o pela largura do intervalo, e é uma
 * decisão melhor informada do que a de quem está a olhar para o ecrã.
 */
export function RangePicker({
  valor,
  aoMudar,
}: {
  valor: Intervalo;
  aoMudar: (i: Intervalo) => void;
}): ReactNode {
  const livre = valor.rotulo === PERSONALIZADO;
  const [aberto, setAberto] = useState(livre);
  const [campoDe, setCampoDe] = useState(() => paraCampo(valor.de));
  const [campoAte, setCampoAte] = useState(() => paraCampo(valor.ate));
  const [erro, setErro] = useState<string | null>(null);

  // Os campos partem sempre do intervalo em vigor: quem carrega em "24 h" e
  // depois abre o editor quer afinar essas 24 h, não recomeçar do nada.
  useEffect(() => {
    setCampoDe(paraCampo(valor.de));
    setCampoAte(paraCampo(valor.ate));
    setErro(null);
  }, [valor.de, valor.ate]);

  function aplicar(): void {
    const de = ler(campoDe);
    const ate = ler(campoAte);

    if (!de.ok || !ate.ok) {
      const vazio = (!de.ok && de.motivo === "vazio") || (!ate.ok && ate.motivo === "vazio");
      setErro(vazio ? "Preenche as duas datas." : "Data inválida — o ano tem 4 dígitos.");
      return;
    }
    if (de.data >= ate.data) {
      setErro("O início tem de ser anterior ao fim.");
      return;
    }
    if (ate.data.getTime() - de.data.getTime() > MAX_DIAS * 86_400_000) {
      setErro(`O intervalo não pode passar de ${MAX_DIAS} dias.`);
      return;
    }

    setErro(null);
    aoMudar({ rotulo: PERSONALIZADO, de: de.data, ate: ate.data });
  }

  return (
    <div>
      <div className="filtros" role="group" aria-label="Intervalo do histórico">
        {PRESETS.map((p) => (
          <button
            key={p.rotulo}
            type="button"
            aria-pressed={valor.rotulo === p.rotulo}
            onClick={() => {
              setAberto(false);
              aoMudar(intervaloDe(p.rotulo, p.horas));
            }}
          >
            {p.rotulo}
          </button>
        ))}
        <button
          type="button"
          aria-pressed={livre}
          aria-expanded={aberto}
          onClick={() => setAberto((a) => !a)}
        >
          Datas…
        </button>
      </div>

      {aberto && (
        <div className="intervalo-livre">
          <label>
            <span>Início</span>
            <input
              type="datetime-local"
              value={campoDe}
              max={campoAte}
              onChange={(e) => setCampoDe(e.target.value)}
            />
          </label>
          <label>
            <span>Fim</span>
            <input
              type="datetime-local"
              value={campoAte}
              min={campoDe}
              onChange={(e) => setCampoAte(e.target.value)}
            />
          </label>
          {/* Só ao aplicar. A cada tecla eram quatro pedidos ao servidor por
              uma data que ainda está a meio de ser escrita. */}
          <button type="button" className="aplicar" onClick={aplicar}>
            Aplicar
          </button>
          <p className="nota-livre">
            Em {ROTULO_FUSO}. {erro === null ? "Máximo de 31 dias." : ""}
          </p>
          {erro !== null && (
            <p className="erro-livre" role="alert">
              {erro}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
